"use client";

import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";

import { BindableInput } from "./BindableInput";
import { Input } from "@/components/shadcn/input";
import { Button } from "@/components/shadcn/button";
import { Banner } from "@/components/Banner";
import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { cn } from "@/lib/utils";
import { useAtomValue } from "jotai";
import { nodeDataByIdAtom, nodeRegistryAtom } from "@/store/editor-v2";
import type { NodeDefinition } from "@/generated";
import { pathKeysFor, pathRowsFor, type PathValueRow } from "./pathValues";
import { WHOLE_VAR_REF_PATTERN } from "./variableRefs";

// Pool keys are `<node>.<path>.<name>` — names must be dot-free identifiers.
// Mirrors backend Rule 10; the sanitizer prevents most violations at the
// source instead of surfacing them as validation errors after the fact.
function sanitizeName(raw: string): string {
  const cleaned = raw.trim().replace(/[^a-zA-Z0-9_]/g, "_");
  return /^[0-9]/.test(cleaned) ? `_${cleaned}` : cleaned;
}

/**
 * V2 renderer for `display=ConfigPathValues` (`path_values` config): declared
 * per-path values, one section per output path the node currently has. Each
 * row is (name → exactly one `:var[ref]` to a list); the engine materializes
 * it as an aligned path-scoped variable `<node>.<path>.<name>` holding only
 * the items that took that path.
 *
 * Rows are variable-only and list-only in the editor even though the engine
 * accepts any literal-or-one-ref value: a scalar or a literal is the same on
 * every item, so it's already reachable downstream of every path and gains
 * nothing from a per-path projection — only per-item lists do.
 */
export function PathValuesField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  description,
  disabled,
  error,
}: {
  nodeId: string;
  fieldKey: string;
  label?: string;
  tooltip?: string;
  description?: string;
  disabled?: boolean;
  error?: string;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path) as Record<string, PathValueRow[]> | undefined;
  const nodeData = useAtomValue(nodeDataByIdAtom(nodeId));
  const nodeRegistry = useAtomValue(nodeRegistryAtom) as NodeDefinition[];

  // Watch the sibling fields that shape the path set so sections appear and
  // disappear live as the user edits branches/routes/the failure action.
  const liveBranches = form.watch(`${nodeId}.branches`) as unknown;
  const liveRoutes = form.watch(`${nodeId}.routes`) as unknown;
  const liveErrorAction = form.watch(`${nodeId}.on_error_action`) as unknown;
  const liveConfig = useMemo(
    () => ({
      ...(nodeData?.nodeConfig as Record<string, unknown> | undefined),
      ...(liveBranches !== undefined ? { branches: liveBranches } : {}),
      ...(liveRoutes !== undefined ? { routes: liveRoutes } : {}),
      ...(liveErrorAction !== undefined
        ? { on_error_action: liveErrorAction }
        : {}),
    }),
    [nodeData?.nodeConfig, liveBranches, liveRoutes, liveErrorAction]
  );
  const registryEntry = nodeRegistry.find((r) => r.type === nodeData?.nodeType);
  const pathInfos = pathKeysFor(nodeData?.nodeType, registryEntry, liveConfig);

  const rowsByPath = useMemo(
    () => pathRowsFor({ [fieldKey]: raw } as Record<string, unknown>),
    [raw, fieldKey]
  );

  const write = (next: Record<string, PathValueRow[]>) =>
    form.setValue(path, next, { shouldDirty: true, shouldTouch: true });

  const patchRow = (
    pathKey: string,
    index: number,
    patch: Partial<PathValueRow>
  ) => {
    const rows = [...(rowsByPath[pathKey] ?? [])];
    // Two rows sharing a name would collide in the emitted output key
    // `<node>.<path>.<name>`, silently dropping one — refuse the rename and
    // let the field snap back, matching BindableDictRowsField.
    if (
      patch.name !== undefined &&
      rows.some((r, i) => i !== index && r.name === patch.name && r.name !== "")
    ) {
      return;
    }
    rows[index] = { ...rows[index], ...patch };
    write({ ...rowsByPath, [pathKey]: rows });
  };

  const removeRow = (pathKey: string, index: number) => {
    const rows = (rowsByPath[pathKey] ?? []).filter((_, i) => i !== index);
    const next = { ...rowsByPath };
    if (rows.length > 0) next[pathKey] = rows;
    else delete next[pathKey];
    write(next);
  };

  const addRow = (pathKey: string) => {
    const rows = rowsByPath[pathKey] ?? [];
    write({ ...rowsByPath, [pathKey]: [...rows, { name: "", value: "" }] });
  };

  if (pathInfos.length === 0) return null;

  const invalid = !!error;

  return (
    <div className="flex flex-col gap-2">
      {label ? (
        <NoxusFormLabel
          label={label}
          tooltip={tooltip}
          name={fieldKey}
          noMargin
          isDisabled={disabled}
        />
      ) : null}
      {description ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      <div
        className={cn(
          "flex w-full flex-col gap-3",
          invalid && "rounded-md ring-1 ring-inset ring-amber-400/70 p-1.5"
        )}
      >
        {pathInfos.map((info) => {
          const rows = rowsByPath[info.key] ?? [];
          return (
            <div
              key={info.key}
              className="flex flex-col gap-2 rounded-md border border-border/60 p-2"
            >
              <div className="flex items-center justify-between">
                <span
                  className="text-xs font-medium capitalize text-muted-foreground"
                  title={info.label}
                >
                  {info.label}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  className="h-7 gap-1 px-2 text-xs text-muted-foreground"
                  onClick={() => addRow(info.key)}
                >
                  <Plus className="size-3.5" />
                  Add value
                </Button>
              </div>
              {rows.length === 0 ? (
                <div className="rounded-md border border-dashed border-border px-3 py-3 text-center text-xs text-muted-foreground">
                  No declared values: nodes on this path can still reference any
                  raw output.
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {rows.map((row, index) => (
                    <PathValueRowEditor
                      // Keyed on the row's own name, not its index: the Name
                      // field is uncontrolled, so an index key lets React reuse
                      // the input after a delete and every following row keeps
                      // showing the previous occupant's name.
                      key={`${info.key}-${row.name || `unnamed-${index}`}`}
                      nodeId={nodeId}
                      row={row}
                      disabled={disabled}
                      onPatch={(patch) => patchRow(info.key, index, patch)}
                      onRemove={() => removeRow(info.key, index)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {error ? <Banner label={error} style="warning" size="sm" /> : null}
    </div>
  );
}

function PathValueRowEditor({
  nodeId,
  row,
  disabled,
  onPatch,
  onRemove,
}: {
  nodeId: string;
  row: PathValueRow;
  disabled?: boolean;
  onPatch: (patch: Partial<PathValueRow>) => void;
  onRemove: () => void;
}) {
  const incomplete = rowIncompleteness(row);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start gap-1.5">
        <Input
          disabled={disabled}
          defaultValue={row.name}
          placeholder="Name"
          className={cn(
            "h-8 w-2/5 min-w-0 shrink-0 bg-background",
            incomplete?.nameMissing && "ring-1 ring-inset ring-amber-400/70"
          )}
          onBlur={(e) => {
            const name = sanitizeName(e.target.value);
            if (name !== row.name) onPatch({ name });
            if (name !== e.target.value) e.target.value = name;
          }}
        />
        <div className="min-w-0 flex-1">
          <BindableInput
            nodeId={nodeId}
            value={row.value}
            disabled={disabled}
            invalid={!!incomplete?.valueMissing}
            variableOnly
            isList
            placeholder="Select a list variable…"
            onChange={(next) => onPatch({ value: next })}
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Remove ${row.name || "value"}`}
          disabled={disabled}
          className="size-8 shrink-0 text-muted-foreground"
          onClick={onRemove}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>
      {incomplete ? (
        <Banner label={incomplete.message} style="warning" size="sm" />
      ) : null}
    </div>
  );
}

interface RowIncompleteness {
  nameMissing: boolean;
  /** Empty, or a literal — the editor accepts exactly one variable. */
  valueMissing: boolean;
  message: string;
}

/** Why a row can't be emitted yet, or null when it's complete. The engine
 * silently drops a nameless row and would carry a literal unchanged, so both
 * are surfaced here rather than discovered on the canvas after a run. */
function rowIncompleteness(row: PathValueRow): RowIncompleteness | null {
  const nameMissing = row.name === "";
  const hasValue = row.value !== "";
  const isVariable = hasValue && WHOLE_VAR_REF_PATTERN.test(row.value);
  const valueMissing = !isVariable;
  if (!nameMissing && !valueMissing) return null;
  let message: string;
  if (hasValue && !isVariable) {
    message = nameMissing
      ? "Give this value a name. Only a variable is accepted here. Pick a list variable."
      : "Only a variable is accepted here. Pick a list variable.";
  } else if (nameMissing && valueMissing) {
    message = "Give this value a name and pick a list variable.";
  } else if (nameMissing) {
    message = "Give this value a name.";
  } else {
    message = "Pick a list variable.";
  }
  return { nameMissing, valueMissing, message };
}
